type RoutePathLike = {
  layout?: string;
  path?: string;
};

const CANONICAL_ROOTS = new Set([
  'admin',
  'auth',
  'objects',
  'pages',
  'public',
  'rtl',
  'tools',
]);

const trimTrailingSlashes = (value: string): string => {
  if (!value) return '';
  if (value === '/') return '/';
  return value.replace(/\/+$/, '') || '/';
};

const normalizeLeadingSlash = (value: string): string => {
  if (!value) return '';
  return value.startsWith('/') ? value : `/${value}`;
};

const joinRouteParts = (layout: string, path: string): string => {
  const normalizedLayout = trimTrailingSlashes(normalizeLeadingSlash(layout || ''));
  const normalizedPath = path ? path.replace(/^\/+/, '') : '';

  if (!normalizedLayout || normalizedLayout === '/') {
    return normalizedPath ? `/${normalizedPath}` : '/';
  }

  if (!normalizedPath) {
    return normalizedLayout;
  }

  return `${normalizedLayout}/${normalizedPath}`;
};

const hasCanonicalRoot = (path: string): boolean => {
  const normalizedPath = normalizeLeadingSlash(path || '');
  const firstSegment = normalizedPath.replace(/^\/+/, '').split('/')[0]?.toLowerCase();
  return Boolean(firstSegment && CANONICAL_ROOTS.has(firstSegment));
};

export const resolveRoutePath = (route: RoutePathLike): string => {
  const path = route?.path || '';
  if (!path) return '';

  if (path.startsWith('/') && hasCanonicalRoot(path)) {
    return trimTrailingSlashes(path);
  }

  return trimTrailingSlashes(joinRouteParts(route?.layout || '', path));
};

export const stripOptionalRouteSuffix = (path: string): string => {
  if (!path) return '';
  return path.replace(/\/:[^/]+\?$/, '');
};

export const normalizeLegacyAdminSectionPath = (path: string): string => {
  if (!path) return path;

  return path
    .replace(/^\/admin\/pages(?=\/|$)/i, '/pages')
    .replace(/^\/admin\/tools(?=\/|$)/i, '/tools')
    .replace(/^\/admin\/objects(?=\/|$)/i, '/objects');
};
